import { useCallback, useEffect, useMemo, useState } from "react";
import { Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { getRequestMetrics } from "../../api/monitoringAPI";

const WINDOWS = [
  { value: 15, label: "15 мин" },
  { value: 60, label: "1 сағ" },
  { value: 360, label: "6 сағ" },
  { value: 1440, label: "24 сағ" },
  { value: 10080, label: "7 күн" },
];
const PLATFORMS = [
  { value: "", label: "Барлығы" },
  { value: "ios", label: "iOS" },
  { value: "android", label: "Android" },
  { value: "web", label: "Web" },
];
const SOURCES = [
  { value: "server", label: "Прод" },
  { value: "srv-test", label: "Тест" },
];

const ms = (v) => (v == null ? "—" : v >= 1000 ? `${(v / 1000).toFixed(2)} с` : `${Math.round(v)} мс`);
const msClass = (v) => (v == null ? "" : v >= 1500 ? "text-rose-500" : v >= 600 ? "text-amber-500" : "text-emerald-500");

function Seg({ options, value, onChange }) {
  return (
    <div className="inline-flex rounded-lg border border-border-primary p-0.5">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={`rounded-md px-3 py-1.5 text-sm ${value === o.value ? "bg-accent-grad text-white" : "text-text-secondary hover:bg-bg-tertiary"}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Kpi({ label, value, hint, tone }) {
  return (
    <div className="rounded-xl border border-border-primary bg-bg-card p-4">
      <div className="text-xs text-text-muted">{label}</div>
      <div className={`mt-1 text-2xl font-bold ${tone || "text-text-primary"}`}>{value}</div>
      {hint && <div className="mt-0.5 text-xs text-text-muted">{hint}</div>}
    </div>
  );
}

/** Сұраулар мониторингі: бэкендтің әр сұрауы (өңделу уақыты, қателер, endpoint-тер). */
export default function RequestsMonitor() {
  const [minutes, setMinutes] = useState(60);
  const [platform, setPlatform] = useState("");
  const [source, setSource] = useState("server");
  const [auto, setAuto] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState("count");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await getRequestMetrics({ minutes, platform, source }));
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [minutes, platform, source]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!auto) return undefined;
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [auto, load]);

  const endpoints = useMemo(() => {
    const list = [...(data?.endpoints || [])];
    const key = { count: "count", p95: "p95_ms", errors: "errors_5xx" }[sortBy];
    return list.sort((a, b) => (b[key] ?? -1) - (a[key] ?? -1));
  }, [data, sortBy]);

  // 24 сағаттан ұзын терезеде осьте күн көрсетеміз (бұрын тек сағат еді — 7 күн бір күн сияқты көрінетін)
  const multiDay = minutes > 1440;
  const series = (data?.series || []).map((p) => {
    const d = new Date(p.t);
    return {
      ...p,
      label: multiDay
        ? d.toLocaleDateString("ru-RU", { day: "numeric", month: "short" })
        : d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" }),
      full: d.toLocaleString("ru-RU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }),
    };
  });
  // Көп күнде — әр күннің бірінші нүктесінде бір ғана белгі
  const dayTicks = multiDay ? series.filter((p, i) => i === 0 || p.label !== series[i - 1].label).map((p) => p.t) : undefined;
  const s = data?.summary;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <Seg options={WINDOWS} value={minutes} onChange={setMinutes} />
        <Seg options={PLATFORMS} value={platform} onChange={setPlatform} />
        <Seg options={SOURCES} value={source} onChange={setSource} />
        <label className="ml-auto flex items-center gap-2 text-sm text-text-secondary">
          <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} className="accent-[#4F45E5]" />
          30 с сайын жаңарту
        </label>
        <button type="button" onClick={load} className="rounded-lg border border-border-primary px-3 py-1.5 text-sm text-text-secondary hover:bg-bg-tertiary">
          {loading ? "Жүктелуде…" : "Жаңарту"}
        </button>
      </div>

      {error && <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-500">{error}</div>}

      {s && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
          <Kpi label="Сұраулар" value={s.requests.toLocaleString("ru-RU")} hint={`${s.rpm} / мин`} />
          <Kpi label="Орташа" value={ms(s.avg_ms)} tone={msClass(s.avg_ms)} />
          <Kpi label="p50 (медиана)" value={ms(s.p50_ms)} tone={msClass(s.p50_ms)} />
          <Kpi label="p95" value={ms(s.p95_ms)} tone={msClass(s.p95_ms)} hint="95% сұрау осыдан жылдам" />
          <Kpi label="5xx қателер" value={s.errors_5xx} tone={s.errors_5xx ? "text-rose-500" : ""} hint={`${(s.error_rate * 100).toFixed(2)}%`} />
          <Kpi label="4xx" value={s.errors_4xx} />
          <Kpi label="Қолданушылар" value={s.users} hint="токенмен кіргендер" />
        </div>
      )}

      {series.length > 0 && (
        <div className="rounded-xl border border-border-primary bg-bg-card p-4">
          <div className="mb-2 text-sm font-semibold text-text-primary">Сұраулар саны мен p95 уақыты</div>
          <div className="h-64">
            <ResponsiveContainer>
              <ComposedChart data={series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#8884" />
                <XAxis
                  dataKey="t"
                  ticks={dayTicks}
                  interval={multiDay ? 0 : "preserveStartEnd"}
                  minTickGap={multiDay ? 0 : 16}
                  tickFormatter={(t) => series.find((p) => p.t === t)?.label ?? ""}
                  tick={{ fontSize: 11 }}
                />
                <YAxis yAxisId="c" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="ms" orientation="right" tick={{ fontSize: 11 }} unit=" мс" />
                <Tooltip labelFormatter={(t) => series.find((p) => p.t === t)?.full ?? t} />
                <Bar yAxisId="c" dataKey="count" name="Сұраулар" fill="#4F45E5" />
                <Bar yAxisId="c" dataKey="errors" name="5xx" fill="#f43f5e" />
                <Line yAxisId="ms" dataKey="p95_ms" name="p95, мс" stroke="#f59e0b" dot={false} strokeWidth={2} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="grid gap-5 xl:grid-cols-3">
        <div className="rounded-xl border border-border-primary bg-bg-card xl:col-span-2">
          <div className="flex items-center justify-between border-b border-border-primary p-4">
            <div className="text-sm font-semibold text-text-primary">Endpoint-тер</div>
            <Seg
              options={[{ value: "count", label: "Саны" }, { value: "p95", label: "Баяулығы" }, { value: "errors", label: "Қателер" }]}
              value={sortBy}
              onChange={setSortBy}
            />
          </div>
          <div className="max-h-[520px] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-bg-card text-left text-xs text-text-muted">
                <tr>
                  <th className="px-4 py-2">Endpoint</th>
                  <th className="px-2 py-2 text-right">Саны</th>
                  <th className="px-2 py-2 text-right">p50</th>
                  <th className="px-2 py-2 text-right">p95</th>
                  <th className="px-2 py-2 text-right">max</th>
                  <th className="px-4 py-2 text-right">5xx / 4xx</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-primary">
                {endpoints.map((e) => (
                  <tr key={`${e.method} ${e.endpoint}`} className="hover:bg-bg-tertiary/60">
                    <td className="px-4 py-2 font-mono text-xs text-text-primary">
                      <span className="mr-2 rounded bg-bg-tertiary px-1.5 py-0.5 text-[10px] text-text-secondary">{e.method}</span>
                      {e.endpoint}
                    </td>
                    <td className="px-2 py-2 text-right text-text-primary">{e.count}</td>
                    <td className={`px-2 py-2 text-right ${msClass(e.p50_ms)}`}>{ms(e.p50_ms)}</td>
                    <td className={`px-2 py-2 text-right font-semibold ${msClass(e.p95_ms)}`}>{ms(e.p95_ms)}</td>
                    <td className="px-2 py-2 text-right text-text-muted">{ms(e.max_ms)}</td>
                    <td className="px-4 py-2 text-right">
                      <span className={e.errors_5xx ? "font-semibold text-rose-500" : "text-text-muted"}>{e.errors_5xx}</span>
                      <span className="text-text-muted"> / {e.errors_4xx}</span>
                    </td>
                  </tr>
                ))}
                {!endpoints.length && (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-text-muted">Бұл кезеңде сұрау жоқ</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="space-y-5">
          <div className="rounded-xl border border-border-primary bg-bg-card p-4">
            <div className="mb-3 text-sm font-semibold text-text-primary">Ең баяу (p95, ≥5 сұрау)</div>
            <ul className="space-y-2">
              {(data?.slowest || []).map((e) => (
                <li key={`${e.method} ${e.endpoint}`} className="flex items-center justify-between gap-2 text-xs">
                  <span className="truncate font-mono text-text-secondary" title={e.endpoint}>{e.method} {e.endpoint}</span>
                  <span className={`shrink-0 font-semibold ${msClass(e.p95_ms)}`}>{ms(e.p95_ms)}</span>
                </li>
              ))}
              {!data?.slowest?.length && <li className="text-xs text-text-muted">—</li>}
            </ul>
          </div>
          <div className="rounded-xl border border-border-primary bg-bg-card p-4">
            <div className="mb-3 text-sm font-semibold text-text-primary">Платформалар</div>
            <ul className="space-y-2 text-sm">
              {(data?.platforms || []).map((p) => (
                <li key={p.platform} className="flex justify-between">
                  <span className="text-text-secondary">{p.platform}</span>
                  <span className="text-text-primary">{p.count} · <span className={msClass(p.p95_ms)}>p95 {ms(p.p95_ms)}</span></span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-xl border border-border-primary bg-bg-card p-4">
            <div className="mb-3 text-sm font-semibold text-text-primary">Статус кодтары</div>
            <div className="flex flex-wrap gap-2">
              {(data?.statuses || []).map((st) => (
                <span
                  key={st.status}
                  className={`rounded-md px-2 py-1 text-xs ${st.status >= 500 ? "bg-rose-500/15 text-rose-500" : st.status >= 400 ? "bg-amber-500/15 text-amber-500" : "bg-emerald-500/15 text-emerald-500"}`}
                >
                  {st.status}: {st.count}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
